import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin, Clock, DollarSign, ArrowRight, Zap,
  Heart, Globe, Users, Shield, Code2, BarChart3,
  CheckCircle, Coffee, Laptop, Plane, BookOpen,
} from 'lucide-react';
import PageLayout from '../components/PageLayout';

const DEPARTMENTS = ['All', 'Engineering', 'Research', 'Product', 'Growth', 'Operations'];

const ROLES = [
  {
    id: 1,
    title: 'Senior Backend Engineer',
    dept: 'Engineering',
    location: 'Remote (US/EU)',
    type: 'Full-time',
    salary: '$160K – $210K',
    icon: Code2,
    accent: '#00E5FF',
    desc: 'Own the core API infrastructure powering wallet risk analysis at scale. You\'ll work on high-throughput data pipelines, Etherscan integration, and our real-time risk engine.',
    skills: ['Node.js', 'MongoDB', 'Redis', 'Docker', 'Blockchain APIs'],
  },
  {
    id: 2,
    title: 'Blockchain Security Researcher',
    dept: 'Research',
    location: 'Remote (Worldwide)',
    type: 'Full-time',
    salary: '$140K – $190K',
    icon: Shield,
    accent: '#7C3AED',
    desc: 'Discover new fraud patterns, analyze on-chain exploit signatures, and improve our risk scoring model. Your research directly shapes the intelligence we deliver to 500+ security teams.',
    skills: ['Smart Contracts', 'Solidity', 'DeFi', 'Python', 'Data Analysis'],
  },
  {
    id: 3,
    title: 'Senior Frontend Engineer',
    dept: 'Engineering',
    location: 'Remote (US/EU)',
    type: 'Full-time',
    salary: '$140K – $185K',
    icon: Code2,
    accent: '#f59e0b',
    desc: 'Build the dashboard, data visualizations, and developer tools that make complex blockchain data intuitive. You care deeply about UX and performance.',
    skills: ['React', 'TypeScript', 'TailwindCSS', 'D3.js', 'REST APIs'],
  },
  {
    id: 4,
    title: 'Product Manager — Developer Platform',
    dept: 'Product',
    location: 'Remote (US)',
    type: 'Full-time',
    salary: '$145K – $185K',
    icon: BarChart3,
    accent: '#10b981',
    desc: 'Define and ship the roadmap for our REST API, webhooks, and developer tooling. You\'ll work closely with engineers and talk to customers weekly.',
    skills: ['API Products', 'Developer Experience', 'Data Products', 'Roadmapping'],
  },
  {
    id: 5,
    title: 'Growth Marketing Manager',
    dept: 'Growth',
    location: 'Remote (US)',
    type: 'Full-time',
    salary: '$110K – $145K',
    icon: Zap,
    accent: '#f43f5e',
    desc: 'Own top-of-funnel growth across content, SEO, developer marketing, and partnerships. You understand both the crypto ecosystem and developer audiences.',
    skills: ['Content Marketing', 'SEO', 'Developer Marketing', 'Analytics', 'Web3'],
  },
  {
    id: 6,
    title: 'Compliance & AML Specialist',
    dept: 'Operations',
    location: 'Remote (US/UK)',
    type: 'Full-time',
    salary: '$120K – $160K',
    icon: Globe,
    accent: '#8b5cf6',
    desc: 'Bridge the gap between our technical capabilities and the regulatory needs of our enterprise customers. Define compliance frameworks, support enterprise deals, and shape our FATF/AML features.',
    skills: ['AML/KYC', 'FATF', 'Blockchain Compliance', 'Regulatory Affairs'],
  },
];

const PERKS = [
  { icon: DollarSign, title: 'Competitive Salary',    desc: 'Top-of-market cash comp + meaningful equity. We believe in sharing the upside with the team.' },
  { icon: Globe,      title: 'Fully Remote',          desc: 'Work from anywhere. We have teammates across 12 countries and async-first culture.' },
  { icon: Laptop,     title: '$3K Home Office Setup', desc: 'Full allowance to build the workspace you need to do your best work.' },
  { icon: BookOpen,   title: '$2K Learning Budget',   desc: 'Annual budget for conferences, courses, books, and anything that sharpens your skills.' },
  { icon: Plane,      title: 'Team Offsites',         desc: 'Twice-yearly team meetups in great cities. We work remotely but bond in person.' },
  { icon: Heart,      title: 'Health & Wellness',     desc: 'Full medical, dental, and vision. Plus a monthly wellness stipend for gym, therapy, or whatever keeps you healthy.' },
  { icon: Coffee,     title: 'Async Culture',         desc: 'No mandatory stand-ups, sensible meeting culture. Deep work time is protected and respected.' },
  { icon: Users,      title: 'Small Team, Big Impact', desc: 'Join a tight team where your work is immediately visible and your voice is genuinely heard.' },
];

function RoleCard({ role }) {
  const { title, dept, location, type, salary, icon: Icon, accent, desc, skills } = role;
  return (
    <div className="glass-card border border-white/[0.06] p-6 group hover:-translate-y-1 transition-all duration-300">
      <div className="flex items-start gap-4 mb-4">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
          style={{ background: `${accent}15`, border: `1px solid ${accent}25` }}
        >
          <Icon size={18} style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-white mb-1 leading-snug">{title}</h3>
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: `${accent}12`, color: accent }}
          >
            {dept}
          </span>
        </div>
      </div>

      <p className="text-slate-500 text-sm leading-relaxed mb-5">{desc}</p>

      <div className="flex flex-wrap gap-1.5 mb-5">
        {skills.map(s => (
          <span key={s} className="text-[10px] text-slate-500 bg-white/[0.04] border border-white/[0.07] px-2 py-0.5 rounded-full">{s}</span>
        ))}
      </div>

      <div className="flex flex-wrap gap-4 text-xs text-slate-500 mb-5">
        <span className="flex items-center gap-1.5"><MapPin size={11} /> {location}</span>
        <span className="flex items-center gap-1.5"><Clock size={11} /> {type}</span>
        <span className="flex items-center gap-1.5"><DollarSign size={11} /> {salary}</span>
      </div>

      <button
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold border transition-all group-hover:gap-3"
        style={{
          background: `${accent}12`,
          color: accent,
          borderColor: `${accent}25`,
        }}
      >
        Apply for this Role <ArrowRight size={13} />
      </button>
    </div>
  );
}

export default function Careers() {
  const [activeDept, setActiveDept] = useState('All');

  const filtered = activeDept === 'All' ? ROLES : ROLES.filter(r => r.dept === activeDept);

  return (
    <PageLayout
      badge="We're Hiring"
      badgeColor="green"
      title={<>Help us make Web3<br /><span className="text-green-400">safer for everyone</span></>}
      subtitle="We're a small, remote-first team building the security intelligence layer for the decentralized web. If that excites you, we'd love to meet you."
    >
      {/* Culture highlight */}
      <section className="py-16 bg-[#080c16]/60 border-y border-white/[0.05]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-green-400 bg-green-500/10 border border-green-500/20 px-3 py-1.5 rounded-full mb-5">
                <Heart size={11} /> Our Culture
              </div>
              <h2 className="text-3xl font-bold text-white mb-5 leading-tight">
                Remote-first, async by default,<br />mission-driven to the core
              </h2>
              <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
                <p>
                  We're a fully distributed team spread across North America, Europe, and Asia.
                  We default to async communication — long-form writing over quick Slack messages,
                  thorough documentation over meetings.
                </p>
                <p>
                  We move fast but we don't burn people out. Deep work is protected. Your evenings
                  and weekends are yours. We measure output, not hours.
                </p>
                <p>
                  Security is our product, but it's also our culture. We think carefully,
                  communicate honestly, and default to transparency with our team and our customers.
                </p>
              </div>
              <div className="mt-7 space-y-3">
                {[
                  'Transparent salary bands — no negotiation games',
                  'Quarterly performance reviews with clear growth paths',
                  'Open calendar culture — default no-meeting Wednesdays',
                  '4-week minimum PTO encouraged (not just offered)',
                ].map(item => (
                  <div key={item} className="flex items-start gap-2 text-sm text-slate-500">
                    <CheckCircle size={13} className="text-green-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Team Size',    value: '18',         sub: 'and growing' },
                { label: 'Countries',    value: '12',         sub: 'team members' },
                { label: 'Remote',       value: '100%',       sub: 'forever policy' },
                { label: 'Glassdoor',    value: '4.9 ★',      sub: 'employer rating' },
              ].map(({ label, value, sub }) => (
                <div key={label} className="glass-card border border-white/[0.06] p-5 text-center">
                  <div className="text-3xl font-black text-white mb-1">{value}</div>
                  <div className="text-xs font-semibold text-green-400 mb-0.5">{label}</div>
                  <div className="text-[10px] text-slate-600">{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Open roles */}
      <section className="py-20 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">Open Positions</h2>
            <p className="text-slate-500 text-sm">{ROLES.length} roles across {DEPARTMENTS.length - 1} departments</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {DEPARTMENTS.map(dept => (
              <button
                key={dept}
                onClick={() => setActiveDept(dept)}
                className={[
                  'px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all',
                  activeDept === dept
                    ? 'bg-green-500/10 text-green-400 border-green-500/25'
                    : 'text-slate-500 border-white/[0.08] hover:text-slate-200 hover:border-white/20',
                ].join(' ')}
              >
                {dept}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-14 text-slate-600">No open roles in this department right now. Check back soon!</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(role => <RoleCard key={role.id} role={role} />)}
          </div>
        )}

        <p className="text-center text-slate-500 text-sm mt-10">
          Don't see your role?{' '}
          <Link to="/contact" className="text-[#00E5FF] hover:underline">Send us an open application →</Link>
        </p>
      </section>

      {/* Perks */}
      <section className="py-20 bg-[#080c16]/60 border-y border-white/[0.05]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/20 px-3 py-1.5 rounded-full mb-4">
              <Zap size={11} /> Benefits & Perks
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">Everything you need to do great work</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PERKS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="glass-card border border-white/[0.06] p-5">
                <div className="w-9 h-9 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center mb-4">
                  <Icon size={15} className="text-[#00E5FF]" />
                </div>
                <div className="text-sm font-bold text-white mb-1.5">{title}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <Users size={28} className="text-green-400 mx-auto mb-5" />
        <h2 className="text-3xl font-bold text-white mb-3">Ready to join the mission?</h2>
        <p className="text-slate-400 text-sm mb-8 max-w-lg mx-auto">
          We review every application carefully. Even if you don't see the perfect fit right now,
          send us a note — we're always interested in exceptional people.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="mailto:careers@zenithcipher.io"
            className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-400 text-black font-bold px-7 py-3.5 rounded-xl transition-all text-sm shadow-lg shadow-green-500/20"
          >
            Send Open Application <ArrowRight size={14} />
          </a>
          <Link
            to="/about"
            className="inline-flex items-center justify-center gap-2 border border-white/10 hover:border-white/20 bg-white/[0.04] text-slate-300 font-medium px-7 py-3.5 rounded-xl transition-all text-sm"
          >
            Learn About Us
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}
